import { uniq } from 'lodash-es';

import { type DisplayInfo } from '@blaze-shared/api-contracts-domain';

interface IntelliValueInfo {
  displayInfos?: DisplayInfo[];
  isFormulaValue?: boolean;
  value?: string;
}

import {
  reverStringifyVariablesInArray,
  stringifyVariableNamesInsideArray,
} from './legacy-code-array-stringifier';

const LOCAL_VARIABLE_TERMINATOR = ';';
const localVariableWithTerminator = /(@@|\$\$|&&)[a-zA-Z0-9_-]+;/g;
const mongoId = /^[a-f0-9]{24}$/i;
export const variableRequiresNormalization = (variableName: string): boolean => {
  const isAlreadyTerminated = /[\]};]$|\.\.$/.test(variableName);
  if (isAlreadyTerminated) {
    return false;
  }
  if (mongoId.test(variableName)) {
    return true;
  }
  return variableName.startsWith('$$') || variableName.startsWith('@@');
};

export const denormalizeCodeBackToLegacy = (code?: string): string => {
  if (!code) {
    return '';
  }
  code = stringifyVariableNamesInsideArray(code);
  return code.replaceAll(localVariableWithTerminator, (match) => {
    if (match.startsWith('&&')) {
      return match.slice(2, -1);
    }
    return match.slice(0, -1);
  });
};

export const normalizeVariableName = (variableName: string): string => {
  if (!variableRequiresNormalization(variableName)) {
    return variableName;
  }
  if (/[a-f0-9]{24}/.test(variableName)) {
    return `&&${variableName};`;
  }
  return variableName + LOCAL_VARIABLE_TERMINATOR;
};
//todo this should be removed in future.. for now we need to cast it to unknown as there are cases, when instead of plain string we receive array - this happens due some
// mechanism that parses value to json if its array in assignments.
export const fixBrokenValueFromAssignments = (value: unknown): string => {
  if (!value) {
    return '';
  }
  if (typeof value !== 'string') {
    return JSON.stringify(value);
  }
  return value;
};

export const normalizeLegacyCode = (
  value: IntelliValueInfo,
  variableNames: string[] = [],
): string => {
  let code = value.value;
  if (!code) {
    return '';
  }
  const displayInfos = value.displayInfos || [];
  const displayValues = [...displayInfos.map(({ value }) => value), ...variableNames];
  const isArray = code.startsWith('[') && code.endsWith(']');

  if (isArray) {
    code = reverStringifyVariablesInArray(code);
  }

  const mongoIds = uniq(displayValues.filter((value) => mongoId.test(value)));

  const variablesRequireTerminations = uniq(
    displayValues.filter((value) => {
      return variableRequiresNormalization(value) && !mongoId.test(value);
    }),
  );

  const escapeDollarSigns = (str: string): string => str.replace(/\$/g, '$$$$');

  code = code.replaceAll(/(\$\$|@@)[a-zA-Z0-9_:;-]+/g, (matchVarName) => {
    if (
      variableRequiresNormalization(matchVarName) &&
      !matchVarName.endsWith(LOCAL_VARIABLE_TERMINATOR)
    ) {
      const foundVariables = variablesRequireTerminations.filter((variable) =>
        matchVarName.includes(variable),
      );
      if (!foundVariables.length) {
        return matchVarName;
      }

      foundVariables.sort((a, b) => b.length - a.length);
      const variable = foundVariables[0];
      const escapedReplacementString = escapeDollarSigns(variable);
      return matchVarName.replace(variable, escapedReplacementString + LOCAL_VARIABLE_TERMINATOR);
    }
    return matchVarName;
  });

  return mongoIds.reduce((acc, variable) => {
    return acc.replaceAll(new RegExp(`(colId:)?${variable}`, 'g'), (match) => {
      if (!match) {
        return match;
      }
      if (match.startsWith('colId:')) {
        return match;
      }
      return `&&${match};`;
    });
  }, code);
};
